import { useState, useEffect, useRef } from 'react';
import { useOutletContext, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/api/axios';
import { PaperPlaneRight, Robot, WhatsappLogo, MessengerLogo, MagnifyingGlass, UserCircleGear, ArrowLeft, List, Bell, Image as ImageIcon, X, ShoppingCart, Sparkle, Check, XCircle, PencilSimple, FloppyDisk, FilePdf, FileVideo, FileAudio, FileText as FileTextIcon, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cldOptimize } from '@/utils/cloudinary';

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'];
const VIDEO_EXTS = ['mp4', '3gp', 'mov', 'webm', 'avi'];
const AUDIO_EXTS = ['mp3', 'ogg', 'm4a', 'aac', 'amr', 'wav', 'opus'];
const PDF_EXTS = ['pdf'];

const getFileExt = (url) => {
  if (!url) return '';
  const clean = url.split('?')[0].split('#')[0];
  const m = clean.match(/\.([a-z0-9]+)$/i);
  return m ? m[1].toLowerCase() : '';
};

const getFileName = (url) => {
  if (!url) return 'archivo';
  return url.split('/').pop().split('?')[0] || 'archivo';
};

// For WhatsApp customers, the platform_id IS the phone number in international
// format (e.g. "5025430xxxx"). For Messenger, it is a PSID — never a phone.
// Returns a pretty "+502 5430 xxxx" or null if we can't confirm it's a phone.
const getCustomerPhone = (customer, conv, fallbackPlatformId) => {
  if (customer?.phone) return customer.phone;
  if (conv && conv.platform !== 'whatsapp') return null;
  if (customer && customer.platform && customer.platform !== 'whatsapp') return null;
  const raw = (conv?.customer_id || fallbackPlatformId || customer?.platform_id || '').toString();
  // Phones are 8–15 digits per E.164. PSIDs are 15–17 digits. Be conservative.
  if (!/^\d{8,15}$/.test(raw)) return null;
  // Pretty format: +<country> <rest grouped in 4s>
  // Known country prefixes used in this CRM (longest first).
  const PREFIXES = ['502', '503', '504', '505', '506', '507', '52', '1'];
  let cc = '';
  let rest = raw;
  for (const p of PREFIXES) {
    if (raw.startsWith(p)) { cc = p; rest = raw.slice(p.length); break; }
  }
  // Group rest in chunks of 4 from the right
  const groups = [];
  for (let i = rest.length; i > 0; i -= 4) {
    groups.unshift(rest.slice(Math.max(0, i - 4), i));
  }
  return cc ? `+${cc} ${groups.join(' ')}` : `+${raw}`;
};

const MediaPreview = ({ url, msgId }) => {
  const ext = getFileExt(url);
  const name = getFileName(url);

  if (IMAGE_EXTS.includes(ext) || (url && url.includes('/image/upload/'))) {
    const thumb = cldOptimize(url, 'f_auto,q_auto,w_600');
    const full = cldOptimize(url, 'f_auto,q_auto');
    return (
      <a href={full} target="_blank" rel="noopener noreferrer" className="block my-1" data-testid={`media-image-${msgId}`}>
        <img src={thumb} alt="Imagen adjunta" loading="lazy" className="max-w-full rounded-md max-h-60 object-contain" />
      </a>
    );
  }

  if (VIDEO_EXTS.includes(ext) || (url && url.includes('/video/upload/') && !AUDIO_EXTS.includes(ext))) {
    const optimized = cldOptimize(url, 'f_auto,q_auto,w_720');
    return (
      <div className="my-1" data-testid={`media-video-${msgId}`}>
        <video src={optimized} controls preload="metadata" className="max-w-full rounded-md max-h-60 bg-black">
          Tu navegador no soporta video HTML5.
        </video>
      </div>
    );
  }

  if (AUDIO_EXTS.includes(ext)) {
    return (
      <div className="my-1 flex items-center gap-2 bg-black/30 rounded-md px-2 py-1.5 min-w-[240px]" data-testid={`media-audio-${msgId}`}>
        <FileAudio size={20} weight="fill" className="text-emerald-400 shrink-0" />
        <audio src={url} controls className="flex-1 h-8">
          Tu navegador no soporta audio HTML5.
        </audio>
      </div>
    );
  }

  if (PDF_EXTS.includes(ext)) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="my-1 flex items-center gap-3 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-md px-3 py-2.5 min-w-[240px] transition-colors"
        data-testid={`media-pdf-${msgId}`}
      >
        <FilePdf size={28} weight="fill" className="text-red-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-white truncate">{name}</div>
          <div className="text-[10px] text-slate-400">PDF · Click para abrir</div>
        </div>
        <DownloadSimple size={16} weight="bold" className="text-slate-400 shrink-0" />
      </a>
    );
  }

  // Generic file fallback
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="my-1 flex items-center gap-3 bg-slate-500/15 hover:bg-slate-500/25 border border-slate-500/30 rounded-md px-3 py-2.5 min-w-[240px] transition-colors"
      data-testid={`media-file-${msgId}`}
    >
      <FileTextIcon size={26} weight="fill" className="text-slate-300 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-bold text-white truncate">{name}</div>
        <div className="text-[10px] text-slate-400">{ext.toUpperCase() || 'Archivo'} · Click para abrir</div>
      </div>
      <DownloadSimple size={16} weight="bold" className="text-slate-400 shrink-0" />
    </a>
  );
};

const ProspectBadge = ({ customer, onAnalyze, analyzing }) => {
  if (!customer) return null;
  const score = customer.prospect_score;
  const level = customer.prospect_level;
  const action = customer.prospect_action;
  const categories = customer.prospect_categories || [];

  if (score === null || score === undefined) {
    return (
      <button
        onClick={onAnalyze}
        disabled={analyzing}
        className="flex items-center gap-1.5 p-2 md:px-3 md:py-2 border border-purple-500/40 text-purple-300 hover:bg-purple-500/15 rounded-full transition-all disabled:opacity-50"
        data-testid="analyze-prospect-btn"
        title="Analizar intención de compra con IA"
      >
        <Sparkle size={16} weight="bold" />
        <span className="hidden md:inline text-xs font-bold">{analyzing ? 'Analizando…' : 'Analizar IA'}</span>
      </button>
    );
  }
  const meta = level === 'hot'
    ? { bg: 'bg-red-500/20', text: 'text-red-300', border: 'border-red-500/40', icon: '🔥' }
    : level === 'warm'
    ? { bg: 'bg-amber-500/20', text: 'text-amber-300', border: 'border-amber-500/40', icon: '🌡️' }
    : { bg: 'bg-slate-500/20', text: 'text-slate-300', border: 'border-slate-500/40', icon: '❄️' };
  const title = action
    ? `${level?.toUpperCase()} · ${action}${categories.length ? `\n[${categories.join(', ')}]` : ''}`
    : `Score: ${score}`;
  return (
    <button
      onClick={onAnalyze}
      disabled={analyzing}
      className={`flex items-center gap-1.5 p-2 md:px-3 md:py-2 border ${meta.border} ${meta.bg} ${meta.text} rounded-full hover:opacity-90 transition-all disabled:opacity-50`}
      data-testid="prospect-badge"
      title={title}
    >
      <span className="text-sm leading-none">{meta.icon}</span>
      <span className="text-xs font-black">{score}</span>
      <span className="hidden md:inline text-[10px] font-bold uppercase tracking-wide opacity-80">{level}</span>
      {analyzing && <span className="text-[10px]">…</span>}
    </button>
  );
};


const Inbox = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedBusiness, setSidebarOpen, totalUnread } = useOutletContext();
  const { isAdmin } = useAuth();
  const { user: currentUser } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [customersMap, setCustomersMap] = useState({});
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const canAssign = isAdmin || currentUser?.role === 'supervisor';
  const canCloseAndFilter = isAdmin || currentUser?.role === 'supervisor';
  const [assignmentFilter, setAssignmentFilter] = useState('all'); // 'all' | 'unassigned' | 'assigned' | 'mine'
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);
  const [closeReason, setCloseReason] = useState('venta');
  const [closing, setClosing] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isOrderDialogOpen, setIsOrderDialogOpen] = useState(false);
  const [orderSuggestion, setOrderSuggestion] = useState(null);
  const [extractingOrder, setExtractingOrder] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [loadingAiSuggestions, setLoadingAiSuggestions] = useState(false);
  const [showAiSuggestions, setShowAiSuggestions] = useState(false);
  const [orderForm, setOrderForm] = useState({
    items: [{ name: '', quantity: 1, price: 0 }],
    payment_method: 'cash',
    delivery_address: '',
    phone1: '',
    phone2: '',
    courier_company: '',
    shipping_cost: 0,
    notes: ''
  });
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [botSummary, setBotSummary] = useState(null);
  const [botSummaryOpen, setBotSummaryOpen] = useState(true);
  const messagesPollingRef = useRef(null);
  const prevMessageCountRef = useRef(0);
  const userScrolledUpRef = useRef(false);
  const messagesContainerRef = useRef(null);

  // Hide Layout's mobile header bar when chat is open on mobile
  useEffect(() => {
    const bar = document.getElementById('mobile-header-bar');
    if (bar) bar.style.display = mobileShowChat ? 'none' : '';
    return () => { if (bar) bar.style.display = ''; };
  }, [mobileShowChat]);

  // Quick reconnect when app returns from background/sleep
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && selectedBusiness) {
        fetchConversations();
        if (selectedConversation) fetchMessages();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [selectedBusiness, selectedConversation]);

  useEffect(() => {
    if (selectedBusiness) {
      fetchConversations();
    }
  }, [assignmentFilter]);

  useEffect(() => {
    if (selectedBusiness) {
      fetchConversations();
      fetchTemplates();
      fetchCustomersMap();
      if (canAssign) fetchUsers();
    }
  }, [selectedBusiness, isAdmin]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages();
      fetchCustomerInfo();
      markAsRead();
      fetchBotSummary(selectedConversation.id);
      messagesPollingRef.current = setInterval(fetchMessages, 3000);
    }
    setIsEditingName(false);
    setEditingNameValue('');
    return () => {
      if (messagesPollingRef.current) clearInterval(messagesPollingRef.current);
    };
  }, [selectedConversation]);

  useEffect(() => {
    // Only auto-scroll if new messages arrived AND user hasn't scrolled up
    if (messages.length > prevMessageCountRef.current && !userScrolledUpRef.current) {
      scrollToBottom();
    }
    prevMessageCountRef.current = messages.length;
  }, [messages]);

  const handleMessagesScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const threshold = 100;
    const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
    userScrolledUpRef.current = !isAtBottom;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const params = { business_id: selectedBusiness.id };
      if (assignmentFilter !== 'all') params.assignment = assignmentFilter;
      const response = await api.get('/conversations', { params });
      setConversations(response.data);
      // Deep-link from Alertas IA / notifications: ?conversation={id} auto-opens the conv
      const targetId = searchParams.get('conversation');
      if (targetId) {
        const match = response.data.find(c => c.id === targetId);
        if (match) {
          setSelectedConversation(match);
          setMobileShowChat(true);
        }
        // Clear the param so it doesn't re-trigger on refetch
        const sp = new URLSearchParams(searchParams);
        sp.delete('conversation');
        setSearchParams(sp, { replace: true });
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const fetchUsers = async () => {
    if (!selectedBusiness) return;
    try {
      const response = await api.get(`/users/by-business/${selectedBusiness.id}`);
      setUsers(response.data.filter(u => u.role !== 'admin'));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleAssignConversation = async (userId) => {
    if (!selectedConversation) return;
    try {
      await api.put(`/admin/conversations/${selectedConversation.id}/assign`, { assigned_to: userId || null });
      toast.success('Conversacion asignada');
      setIsAssignDialogOpen(false);
      await fetchConversations();
    } catch {
      toast.error('Error al asignar conversacion');
    }
  };

  const handleCloseConversation = async () => {
    if (!selectedConversation) return;
    setClosing(true);
    try {
      await api.put(`/conversations/${selectedConversation.id}/close`, { reason: closeReason });
      toast.success('Conversación cerrada');
      setIsCloseDialogOpen(false);
      setSelectedConversation(null);
      setMobileShowChat(false);
      await fetchConversations();
    } catch (error) {
      console.error('Error closing conversation:', error);
      toast.error('Error al cerrar conversación');
    } finally {
      setClosing(false);
    }
  };

  const fetchMessages = async () => {
    if (!selectedConversation) return;
    try {
      const response = await api.get('/messages', { params: { conversation_id: selectedConversation.id } });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchBotSummary = async (convId) => {
    setBotSummary(null);
    setBotSummaryOpen(true);
    try {
      const { data } = await api.get(`/conversations/${convId}/bot-summary`);
      setBotSummary(data.resumen || null);
    } catch { setBotSummary(null); }
  };

  const fetchCustomerInfo = async () => {
    try {
      const response = await api.get(`/customers/${selectedConversation.customer_id}`);
      setCustomerInfo(response.data);
    } catch (error) {
      console.error('Error fetching customer:', error);
    }
  };

  const [analyzingProspect, setAnalyzingProspect] = useState(false);

  const handleAnalyzeProspect = async () => {
    if (!selectedConversation?.customer_id) return;
    setAnalyzingProspect(true);
    try {
      const res = await api.post(`/prospects/analyze/${selectedConversation.customer_id}`);
      // Merge updated prospect block into customerInfo
      setCustomerInfo((prev) => prev ? { ...prev, ...res.data } : prev);
      toast.success(`Analizado: ${res.data.prospect_level?.toUpperCase()} (${res.data.prospect_score})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error analizando con IA');
    } finally {
      setAnalyzingProspect(false);
    }
  };

  const fetchCustomersMap = async () => {
    try {
      const response = await api.get('/customers', { params: { business_id: selectedBusiness.id } });
      const map = {};
      (response.data || []).forEach((c) => { map[c.id] = c; });
      setCustomersMap(map);
    } catch (error) {
      console.error('Error fetching customers map:', error);
    }
  };

  const handleStartEditName = () => {
    setEditingNameValue(customerInfo?.name || '');
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditingNameValue('');
  };

  const handleSaveCustomerName = async () => {
    const newName = editingNameValue.trim();
    if (!newName) {
      toast.error('El nombre no puede estar vacío');
      return;
    }
    if (!selectedConversation?.customer_id) return;
    setSavingName(true);
    try {
      const response = await api.put(`/customers/${selectedConversation.customer_id}`, { name: newName });
      setCustomerInfo(response.data);
      setCustomersMap((prev) => ({ ...prev, [response.data.id]: response.data }));
      setIsEditingName(false);
      setEditingNameValue('');
      toast.success('Nombre actualizado');
    } catch (error) {
      console.error('Error updating customer name:', error);
      toast.error('Error al guardar el nombre');
    } finally {
      setSavingName(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/templates', { params: { business_id: selectedBusiness.id } });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const markAsRead = async () => {
    try {
      await api.put(`/conversations/${selectedConversation.id}/read`);
      fetchConversations();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imagenes');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Imagen demasiado grande (max 25MB)');
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const sendMessage = async (content) => {
    if (!content.trim() && !imageFile) return;
    try {
      setLoading(true);
      let image_url = null;

      if (imageFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', imageFile);
        const uploadRes = await api.post('/uploads/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        image_url = uploadRes.data.url;
        setUploading(false);
      }

      await api.post('/messages', {
        conversation_id: selectedConversation.id,
        sender_type: 'agent',
        content: content.trim() || (image_url ? '[Imagen]' : ''),
        image_url
      });
      setNewMessage('');
      clearImage();
      setShowTemplates(false);
      await fetchMessages();
      await fetchConversations();
      toast.success(image_url ? 'Imagen enviada' : 'Mensaje enviado');
    } catch {
      toast.error('Error al enviar mensaje');
      setUploading(false);
    } finally {
      setLoading(false);
    }
  };

  const requestAiSuggestions = async () => {
    if (!selectedConversation) return;
    if (!messages.length) {
      toast.error('Esta conversación no tiene mensajes aún');
      return;
    }
    try {
      setLoadingAiSuggestions(true);
      setShowAiSuggestions(true);
      const res = await api.post('/ai/asistente/suggest-replies', {
        conversation_id: selectedConversation.id,
      });
      const list = res.data?.suggestions || [];
      setAiSuggestions(list);
      if (!list.length) toast.error('La IA no devolvió sugerencias');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Error generando sugerencias';
      toast.error(detail);
      setShowAiSuggestions(false);
    } finally {
      setLoadingAiSuggestions(false);
    }
  };

  const applyAiSuggestion = (text) => {
    setNewMessage(text);
    setShowAiSuggestions(false);
    setAiSuggestions([]);
  };

  const handleAIResponse = async () => {
    if (!messages.length) { toast.error('No hay mensajes'); return; }
    const lastCustomerMessage = [...messages].reverse().find(m => m.sender_type === 'customer');
    if (!lastCustomerMessage) { toast.error('No hay mensaje del cliente'); return; }
    try {
      setLoading(true);
      const response = await api.post('/ai/chat', {
        business_id: selectedBusiness.id,
        customer_message: lastCustomerMessage.content,
        conversation_id: selectedConversation.id
      });
      await api.post('/messages', {
        conversation_id: selectedConversation.id,
        sender_type: 'bot',
        content: response.data.response
      });
      await fetchMessages();
      await fetchConversations();
      toast.success('Respuesta IA enviada');
    } catch {
      toast.error('Error al obtener respuesta IA');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    setMobileShowChat(true);
    userScrolledUpRef.current = false;
    prevMessageCountRef.current = 0;
    setOrderSuggestion(null);
    setAiSuggestions([]);
    setShowAiSuggestions(false);
  };

  const handleBackToList = () => {
    setMobileShowChat(false);
    setOrderSuggestion(null);
  };

  // Order functions
  const openOrderDialog = (prefill = null) => {
    const form = {
      items: prefill?.items?.length ? prefill.items.map(i => ({
        name: i.name || '', quantity: i.quantity || 1, price: i.price || 0
      })) : [{ name: '', quantity: 1, price: 0 }],
      payment_method: prefill?.payment_method || 'cash',
      delivery_address: prefill?.delivery_address || '',
      phone1: prefill?.phone1 || '',
      phone2: prefill?.phone2 || '',
      courier_company: prefill?.courier_company || '',
      notes: prefill?.notes || ''
    };
    setOrderForm(form);
    setIsOrderDialogOpen(true);
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    if (!orderForm.items.some(i => i.name.trim())) {
      toast.error('Agrega al menos un producto');
      return;
    }
    if (!orderForm.phone1.trim()) {
      toast.error('El telefono 1 es requerido');
      return;
    }
    try {
      const normalizedItems = orderForm.items.map(i => ({
        ...i,
        quantity: Number(i.quantity) || 0,
        price: Number(i.price) || 0,
      }));
      const total = normalizedItems.reduce((s, i) => s + (i.quantity * i.price), 0) + (Number(orderForm.shipping_cost) || 0);
      await api.post('/orders', {
        ...orderForm,
        items: normalizedItems,
        shipping_cost: Number(orderForm.shipping_cost) || 0,
        customer_id: selectedConversation.customer_id,
        business_id: selectedBusiness.id,
        conversation_id: selectedConversation.id,
        total
      });
      toast.success('Pedido creado exitosamente');
      setIsOrderDialogOpen(false);
      setOrderSuggestion(null);
    } catch {
      toast.error('Error al crear pedido');
    }
  };

  const addOrderItem = () => {
    setOrderForm(prev => ({
      ...prev,
      items: [...prev.items, { name: '', quantity: 1, price: 0 }]
    }));
  };

  const removeOrderItem = (idx) => {
    setOrderForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx)
    }));
  };

  const updateOrderItem = (idx, field, value) => {
    setOrderForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  };

  const handleExtractOrder = async () => {
    if (!selectedConversation) return;
    try {
      setExtractingOrder(true);
      const res = await api.post('/ai/extract-order', {
        conversation_id: selectedConversation.id,
        business_id: selectedBusiness.id
      });
      if (res.data.has_order_data) {
        setOrderSuggestion(res.data);
        toast.success('Datos de pedido detectados');
      } else {
        toast.info('No se detectaron datos suficientes para un pedido en esta conversacion');
      }
    } catch {
      toast.error('Error al analizar la conversacion');
    } finally {
      setExtractingOrder(false);
    }
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    return conv.last_message?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (!selectedBusiness) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-sm text-slate-400">Selecciona un negocio para continuar</p>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] md:h-screen flex w-full overflow-x-hidden" data-testid="inbox-page">
      {/* Conversations List */}
      <div className={`w-full md:w-[380px] border-r border-white/5 flex flex-col ${mobileShowChat ? 'hidden md:flex' : 'flex'}`} style={{ background: '#111B21' }}>
        <div className="px-3 py-3 border-b border-white/5" style={{ background: '#1F2C33' }}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl tracking-tight font-bold text-white heading-font">Mensajes</h2>
            <button
              onClick={async () => {
                try {
                  const r = await api.put('/conversations/mark-all-read', null, { params: { business_id: selectedBusiness.id } });
                  toast.success(`${r.data.updated || 0} marcadas como leídas`);
                  await fetchConversations();
                } catch {
                  toast.error('Error al marcar como leídas');
                }
              }}
              className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border border-[#25D366]/30 text-[#25D366] hover:bg-[#25D366]/10 transition-all"
              data-testid="mark-all-read-btn"
              title="Marcar todas como leídas"
            >
              ✓ Leer todas
            </button>
          </div>
          <div className="relative mb-3">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar conversación..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-3 py-2 bg-[#2A3942] text-white placeholder:text-slate-400 text-sm border-0 rounded-full focus:ring-2 focus:ring-[#25D366] focus:outline-none"
              data-testid="search-conversations-input"
            />
          </div>
          {canCloseAndFilter && (
            <div className="flex items-center gap-1 overflow-x-auto pb-1">
              {[
                { key: 'all', label: 'Todas' },
                { key: 'unassigned', label: '⚠ Sin asignar' },
                { key: 'manual_review', label: '🟡 Revisar' },
                { key: 'assigned', label: 'Asignadas' },
                { key: 'mine', label: 'Mías' },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setAssignmentFilter(f.key)}
                  className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-sm border whitespace-nowrap transition-all ${
                    assignmentFilter === f.key
                      ? 'bg-[#1D4ED8] border-[#1D4ED8] text-white shadow-[0_0_10px_rgba(29,78,216,0.4)]'
                      : 'bg-transparent border-white/10 text-slate-400 hover:border-[#1D4ED8]/50 hover:text-white'
                  }`}
                  data-testid={`filter-${f.key}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredConversations.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-400">No hay conversaciones</div>
          ) : (
            filteredConversations.map((conv) => {
              const isHot = conv.unread_count > 0 && conv.last_message_time && (Date.now() - new Date(conv.last_message_time).getTime() > 60 * 60 * 1000);
              const isActive = selectedConversation?.id === conv.id;
              const cust = customersMap[conv.customer_id];
              const custName = cust?.name || `${conv.customer_id?.substring(0, 14)}${conv.customer_id?.length > 14 ? '...' : ''}`;
              // WhatsApp-style: round colored avatar with initials per customer
              const initials = (custName || '?').split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();
              const avatarColors = ['#25D366', '#128C7E', '#075E54', '#34B7F1', '#C5533B', '#F2A900', '#A855F7', '#0EA5E9'];
              const hash = (conv.customer_id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
              const avatarColor = avatarColors[hash % avatarColors.length];
              const isWA = conv.platform === 'whatsapp';
              const PlatformBadge = isWA ? WhatsappLogo : MessengerLogo;
              const platformColor = isWA ? '#25D366' : '#0084FF';
              const phone = isWA ? getCustomerPhone(cust, conv) : null;
              return (
                <div
                  key={conv.id}
                  onClick={() => handleSelectConversation(conv)}
                  className={`px-3 py-2.5 border-b cursor-pointer transition-all duration-150 relative ${
                    isActive ? 'bg-white/10' : 'hover:bg-white/[0.04]'
                  }`}
                  style={{ borderColor: 'rgba(255,255,255,0.05)' }}
                  data-testid={`conversation-item-${conv.id}`}
                >
                  <div className="flex items-center gap-3">
                    {/* WhatsApp-style round avatar with initials + platform dot */}
                    <div className="relative shrink-0">
                      <div
                        className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-white text-sm shadow-sm"
                        style={{ background: avatarColor }}
                      >
                        {initials}
                      </div>
                      <div
                        className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center ring-2"
                        style={{ background: platformColor, ringColor: '#111827' }}
                      >
                        <PlatformBadge size={9} weight="fill" className="text-white" />
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Line 1: name + time */}
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="text-[14px] font-bold text-white truncate leading-tight">{custName}</p>
                        <p className={`text-[10px] shrink-0 font-medium ${conv.unread_count > 0 ? 'text-green-400' : 'text-slate-500'}`}>
                          {new Date(conv.last_message_time).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>

                      {/* Line 2: last message + unread badge */}
                      <div className="flex items-center justify-between gap-2 mt-0.5">
                        <p className={`text-[13px] truncate flex-1 ${conv.unread_count > 0 ? 'text-slate-200 font-medium' : 'text-slate-400'}`}>
                          {conv.last_message || 'Sin mensajes'}
                        </p>
                        {conv.unread_count > 0 && (
                          <span className="bg-[#25D366] text-white text-[10px] font-bold min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full shrink-0">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>

                      {/* Line 3: phone (WA) + badges */}
                      <div className="flex items-center justify-between gap-2 mt-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {phone && (
                            <span className="text-[10px] text-green-400/80 font-medium truncate" data-testid={`conv-phone-${conv.id}`}>
                              📱 {phone}
                            </span>
                          )}
                          {isHot && (
                            <span className="bg-orange-500/20 text-orange-400 border border-orange-500/30 text-[9px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-wide">
                              🔥 Hot
                            </span>
                          )}
                          {conv.atencion_requerida && (
                            <span data-testid={`badge-atencion-${conv.id}`} className="bg-red-500/20 text-red-400 border border-red-500/30 text-[9px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-wide">
                              🚨 Necesita asesor
                            </span>
                          )}
                        </div>
                        {canCloseAndFilter && (
                          conv.assigned_to ? (
                            (() => {
                              const name = conv.assigned_user_name || users.find(u => u.id === conv.assigned_to)?.name;
                              const isOrphan = !name;
                              return (
                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full truncate max-w-[140px] shrink-0 ${
                                  isOrphan
                                    ? 'text-red-400 bg-red-500/10 border border-red-500/30'
                                    : conv.routing_mode === 'single_vendor'
                                    ? 'text-cyan-300 bg-cyan-500/10 border border-cyan-500/30'
                                    : 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/30'
                                }`} title={isOrphan ? 'Vendedor eliminado — re-asigna esta conversación' : `Asignado a: ${name}`}>
                                  {isOrphan
                                    ? '⚠ Eliminado'
                                    : `${conv.routing_mode === 'single_vendor' ? '🚀' : '👤'} ${name}`}
                                </span>
                              );
                            })()
                          ) : conv.routing_state === 'manual_review' ? (
                            <span className="text-[9px] font-bold text-yellow-300 bg-yellow-500/15 border border-yellow-500/40 px-1.5 py-0.5 rounded-full shrink-0">🟡 Revisar</span>
                          ) : (
                            <span className="text-[9px] font-bold text-blue-300 bg-blue-500/10 border border-blue-500/30 px-1.5 py-0.5 rounded-full shrink-0">🤖 Bot</span>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Chat Area */}
      {selectedConversation ? (
        <div className={`flex-1 min-w-0 flex flex-col ${mobileShowChat ? 'flex' : 'hidden md:flex'}`} style={{ background: '#0B141A' }}>
          {/* Chat Header - WhatsApp-style with avatar */}
          <div className="px-3 md:px-4 py-2.5 border-b border-white/10 flex items-center justify-between gap-2 shrink-0 sticky top-0 z-10 min-w-0" style={{ background: '#1F2C33' }}>
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {/* Back button mobile */}
              <button
                onClick={handleBackToList}
                className="md:hidden p-1 shrink-0"
                data-testid="back-to-list-btn"
              >
                <ArrowLeft size={22} weight="bold" className="text-white" />
              </button>
              {/* WhatsApp-style round avatar */}
              {(() => {
                const cName = customerInfo?.name || selectedConversation.customer_id || '?';
                const inits = cName.split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();
                const palette = ['#25D366', '#128C7E', '#075E54', '#34B7F1', '#C5533B', '#F2A900', '#A855F7', '#0EA5E9'];
                const h = (selectedConversation.customer_id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
                return (
                  <div className="relative shrink-0">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white text-sm shadow-sm"
                      style={{ background: palette[h % palette.length] }}
                    >
                      {inits}
                    </div>
                    {selectedConversation.platform === 'whatsapp' ? (
                      <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#25D366] flex items-center justify-center ring-2 ring-[#1F2C33]">
                        <WhatsappLogo size={9} weight="fill" className="text-white" />
                      </div>
                    ) : (
                      <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#0084FF] flex items-center justify-center ring-2 ring-[#1F2C33]">
                        <MessengerLogo size={9} weight="fill" className="text-white" />
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="min-w-0 flex-1">
                {isEditingName ? (
                  <div className="flex items-center gap-1.5">
                    <Input
                      value={editingNameValue}
                      onChange={(e) => setEditingNameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveCustomerName();
                        if (e.key === 'Escape') handleCancelEditName();
                      }}
                      autoFocus
                      placeholder="Nombre del cliente"
                      className="h-7 text-xs bg-slate-800 border-white/10 text-white placeholder:text-slate-500"
                      data-testid="edit-customer-name-input"
                    />
                    <button
                      onClick={handleSaveCustomerName}
                      disabled={savingName}
                      className="p-1.5 bg-green-600 hover:bg-green-500 rounded-sm text-white disabled:opacity-50 shrink-0"
                      data-testid="save-customer-name-btn"
                      title="Guardar"
                    >
                      <FloppyDisk size={14} weight="bold" />
                    </button>
                    <button
                      onClick={handleCancelEditName}
                      disabled={savingName}
                      className="p-1.5 bg-slate-700 hover:bg-slate-600 rounded-sm text-white disabled:opacity-50 shrink-0"
                      data-testid="cancel-edit-customer-name-btn"
                      title="Cancelar"
                    >
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <h3 className="text-sm font-bold text-white truncate" data-testid="customer-name-display">
                      {customerInfo?.name || 'Cliente'}
                    </h3>
                    <button
                      onClick={handleStartEditName}
                      className="p-1 hover:bg-white/10 rounded-sm text-slate-400 hover:text-white transition-colors shrink-0"
                      data-testid="edit-customer-name-btn"
                      title="Editar nombre"
                    >
                      <PencilSimple size={12} weight="bold" />
                    </button>
                  </div>
                )}
                <p className="text-[10px] text-slate-400 flex items-center gap-1.5 flex-wrap" data-testid="chat-header-platform-line">
                  <span>{selectedConversation.platform === 'whatsapp' ? 'WhatsApp' : 'Messenger'}</span>
                  {selectedConversation.platform === 'whatsapp' && (() => {
                    const phone = getCustomerPhone(customerInfo, selectedConversation);
                    if (!phone) return null;
                    return (
                      <>
                        <span className="text-slate-600">·</span>
                        <button
                          type="button"
                          onClick={() => {
                            const raw = phone.replace(/\D/g, '');
                            navigator.clipboard?.writeText(raw).then(() => toast.success('Número copiado'));
                          }}
                          className="text-green-400 hover:text-green-300 font-medium transition-colors"
                          data-testid="chat-header-customer-phone"
                          title="Clic para copiar"
                        >
                          📱 {phone}
                        </button>
                      </>
                    );
                  })()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0 overflow-x-auto scrollbar-thin max-w-[60vw] md:max-w-none">
              <ProspectBadge
                customer={customerInfo}
                onAnalyze={handleAnalyzeProspect}
                analyzing={analyzingProspect}
              />
              {canAssign && (
                <button
                  onClick={() => setIsAssignDialogOpen(true)}
                  className="flex items-center gap-1.5 p-2 md:px-3 md:py-2 border border-white/10 rounded-full hover:bg-white/5 transition-colors"
                  data-testid="assign-conversation-btn"
                  title="Asignar"
                >
                  <UserCircleGear size={18} weight="bold" className="text-[#25D366]" />
                  <span className="hidden md:inline text-xs font-bold text-[#25D366]">Asignar</span>
                </button>
              )}
              <button
                onClick={handleAIResponse}
                disabled={loading}
                className="flex items-center gap-1.5 p-2 md:px-3 md:py-2 bg-[#25D366] hover:bg-[#20B858] text-white rounded-full transition-all disabled:opacity-50"
                data-testid="ai-response-btn"
                title="Respuesta IA"
              >
                <Robot size={18} weight="bold" />
                <span className="hidden md:inline text-xs font-bold">Respuesta IA</span>
              </button>
              <button
                onClick={() => openOrderDialog()}
                className="hidden md:flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-full transition-all"
                data-testid="create-order-from-chat-btn"
                title="Crear Pedido"
              >
                <ShoppingCart size={18} weight="bold" />
                <span className="text-xs font-bold">Pedido</span>
              </button>
              <button
                onClick={() => {
                  const phone = getCustomerPhone(customerInfo, selectedConversation);
                  const params = new URLSearchParams();
                  if (customerInfo?.name) params.set('nombre', customerInfo.name);
                  if (phone) params.set('telefono', phone);
                  if (customerInfo?.address || customerInfo?.direccion) params.set('direccion', customerInfo.address || customerInfo.direccion);
                  if (customerInfo?.email) params.set('email', customerInfo.email);
                  navigate(`/cotizador/nuevo?${params.toString()}`);
                }}
                className="flex items-center gap-1.5 p-2 md:px-3 md:py-2 bg-[#1D4ED8] hover:bg-[#1E40AF] text-white rounded-full transition-all"
                data-testid="create-cotizacion-from-chat-btn"
                title="Crear Cotización CYH"
              >
                <FileTextIcon size={18} weight="bold" />
                <span className="hidden md:inline text-xs font-bold">Cotizar</span>
              </button>
              <button
                onClick={handleExtractOrder}
                disabled={extractingOrder}
                className="hidden md:flex items-center gap-1.5 px-3 py-2 bg-orange-500 hover:bg-orange-400 text-white rounded-full transition-all disabled:opacity-50"
                data-testid="extract-order-btn"
                title="Sugerir Pedido IA"
              >
                <Sparkle size={18} weight="bold" />
                <span className="text-xs font-bold">{extractingOrder ? 'Analizando...' : 'Sugerir Pedido'}</span>
              </button>
              {canCloseAndFilter && (
                <button
                  onClick={() => { setCloseReason('venta'); setIsCloseDialogOpen(true); }}
                  className="flex items-center gap-1.5 p-2 md:px-3 md:py-2 border border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-[12px] transition-all"
                  data-testid="close-conversation-btn"
                  title="Cerrar conversación"
                >
                  <XCircle size={18} weight="bold" />
                  <span className="hidden md:inline text-xs font-bold">Cerrar</span>
                </button>
              )}
              {/* Mobile menu button */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden p-2 hover:bg-white/5 rounded-sm"
                data-testid="chat-menu-btn"
                title="Menu"
              >
                <List size={18} weight="bold" className="text-slate-400" />
              </button>
            </div>
          </div>

          {/* Messages — WhatsApp-style background pattern */}
          <div
            ref={messagesContainerRef}
            onScroll={handleMessagesScroll}
            className="flex-1 overflow-y-auto overflow-x-hidden px-2.5 py-3 md:px-4 md:py-4 space-y-1.5"
            style={{
              background: '#0B141A',
              backgroundImage: 'radial-gradient(circle at 25% 35%, rgba(37,211,102,0.04) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(255,255,255,0.02) 0%, transparent 50%)',
            }}
            data-testid="messages-container"
          >
            {botSummary && botSummaryOpen && (
              <div className="sticky top-0 z-10 mb-2 rounded-xl border border-sky-500/30 bg-[#0f2233]/95 backdrop-blur px-3 py-2.5" data-testid="bot-summary-panel">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[11px] font-black text-sky-400 uppercase tracking-wide">🤖 Resumen del bot con el cliente</p>
                  <button onClick={() => setBotSummaryOpen(false)} data-testid="bot-summary-close"
                    className="text-slate-400 hover:text-white text-sm leading-none px-1">×</button>
                </div>
                <p className="text-[12px] text-slate-200 mt-1 whitespace-pre-line">{botSummary}</p>
              </div>
            )}
            {botSummary && !botSummaryOpen && (
              <button onClick={() => setBotSummaryOpen(true)} data-testid="bot-summary-reopen"
                className="sticky top-0 z-10 mb-2 text-[10px] font-bold text-sky-400 bg-[#0f2233]/95 border border-sky-500/30 rounded-full px-2.5 py-1">
                🤖 Ver resumen del bot
              </button>
            )}
            {messages.map((msg, idx) => {
              const isCustomer = msg.sender_type === 'customer';
              const isBot = msg.sender_type === 'bot';
              const isAgent = msg.sender_type === 'agent';
              const prevMsg = idx > 0 ? messages[idx - 1] : null;
              const isContinuation = prevMsg && prevMsg.sender_type === msg.sender_type;

              return (
                <div
                  key={msg.id}
                  className={`flex ${isCustomer ? 'justify-start' : 'justify-end'} ${isContinuation ? '' : 'mt-2'}`}
                  data-testid={`message-${msg.id}`}
                >
                  <div
                    className={`max-w-[78%] min-w-0 [overflow-wrap:anywhere] break-words px-3 py-1.5 shadow-sm relative ${
                      isCustomer
                        ? `bg-[#1F2C33] text-slate-100 ${isContinuation ? 'rounded-2xl' : 'rounded-2xl rounded-tl-md'}`
                        : isBot
                        ? `bg-[#005C4B] text-white ${isContinuation ? 'rounded-2xl' : 'rounded-2xl rounded-tr-md'}`
                        : `bg-[#005C4B] text-white ${isContinuation ? 'rounded-2xl' : 'rounded-2xl rounded-tr-md'}`
                    }`}
                  >
                    {!isContinuation && (
                      <div className={`flex items-center gap-1.5 mb-0.5 ${isCustomer ? 'text-emerald-400' : 'text-emerald-200/80'}`}>
                        {isCustomer && (
                          <>
                            {selectedConversation.platform === 'whatsapp' ? (
                              <WhatsappLogo size={11} weight="fill" className="text-[#25D366]" />
                            ) : (
                              <MessengerLogo size={11} weight="fill" className="text-[#0084FF]" />
                            )}
                            <span className="text-[10px] font-bold">Cliente</span>
                          </>
                        )}
                        {isBot && (
                          <>
                            <Robot size={11} weight="bold" />
                            <span className="text-[10px] font-bold">Bot IA</span>
                          </>
                        )}
                        {isAgent && <span className="text-[10px] font-bold">{msg.sender_user_name || 'Vendedor'}</span>}
                      </div>
                    )}
                    {msg.image_url && <MediaPreview url={msg.image_url} msgId={msg.id} />}
                    {msg.transcription && (
                      <div className="my-1 px-2 py-1.5 bg-black/30 border-l-2 border-emerald-300/60 rounded-r-md" data-testid={`msg-transcription-${msg.id}`}>
                        <div className="text-[9px] font-black uppercase tracking-wider text-emerald-300/90 mb-0.5 flex items-center gap-1">
                          <Sparkle size={9} weight="bold" /> Transcripción
                        </div>
                        <p className="text-xs italic leading-snug text-slate-100">{msg.transcription}</p>
                      </div>
                    )}
                    {msg.content && msg.content !== '[Imagen]' && !['📷 Imagen','🎥 Video','🎤 Audio','📎 Documento','😀 Sticker'].includes(msg.content) && (
                      <p className="text-[14px] leading-snug whitespace-pre-wrap [overflow-wrap:anywhere] break-words">{msg.content}</p>
                    )}
                    <div className="flex items-center justify-end gap-1 mt-0.5">
                      <p className={`text-[10px] ${isCustomer ? 'text-slate-400' : 'text-emerald-100/70'}`}>
                        {new Date(msg.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                      {!isCustomer && (
                        <span className="text-[11px] text-emerald-200/80" title="Enviado">✓✓</span>
                      )}
                      {(isAgent || isBot) && (
                        <button
                          onClick={() => {
                            setNewMessage('Disculpa, el mensaje anterior fue incorrecto. ');
                            document.querySelector('[data-testid="message-input"]')?.focus();
                          }}
                          className="text-[10px] text-emerald-200/60 hover:text-emerald-100 transition-colors ml-1.5 underline"
                          data-testid={`correct-message-${msg.id}`}
                          title="Enviar corrección"
                        >
                          Corregir
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Order Suggestion Card (only visible to agent) */}
          {orderSuggestion && (
            <div className="mx-3 md:mx-4 mb-2 p-3 bg-[#F59E0B]/10 border border-[#F59E0B] rounded-md shrink-0" data-testid="order-suggestion-card">
              <div className="flex items-center gap-2 mb-2">
                <Sparkle size={18} weight="bold" className="text-[#F59E0B]" />
                <span className="text-sm font-bold text-white">Sugerencia de Pedido IA</span>
              </div>
              <div className="text-xs text-slate-400 space-y-1 mb-3">
                {orderSuggestion.items?.map((item, i) => (
                  <p key={i}>{item.name} x{item.quantity} {item.price > 0 ? `- $${item.price}` : ''}</p>
                ))}
                {orderSuggestion.delivery_address && <p>Direccion: {orderSuggestion.delivery_address}</p>}
                {orderSuggestion.phone1 && <p>Tel: {orderSuggestion.phone1}</p>}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => { openOrderDialog(orderSuggestion); setOrderSuggestion(null); }}
                  className="flex items-center gap-1 px-3 py-1.5 bg-[#16A34A] hover:bg-[#15803D] text-white text-xs font-bold rounded-sm transition-colors"
                  data-testid="accept-order-suggestion-btn"
                >
                  <Check size={14} weight="bold" />
                  Aceptar y Editar
                </button>
                <button
                  onClick={() => setOrderSuggestion(null)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-bold rounded-sm transition-colors"
                  data-testid="reject-order-suggestion-btn"
                >
                  <XCircle size={14} weight="bold" />
                  Descartar
                </button>
              </div>
            </div>
          )}

          {/* Templates Bar */}
          {showTemplates && templates.length > 0 && (
            <div className="p-2 border-t border-white/10 bg-[#0F172A] shrink-0">
              <div className="flex gap-2 overflow-x-auto">
                {templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => { setNewMessage(template.content); setShowTemplates(false); }}
                    className="px-3 py-1 bg-[#111827] border border-white/10 text-xs text-white rounded-sm hover:bg-[#1D4ED8] hover:text-white transition-colors whitespace-nowrap"
                    data-testid={`template-${template.id}`}
                  >
                    {template.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area - with extra bottom padding for iPhone safe area + Made with Emergent badge */}
          <div className="p-2 md:p-3 border-t border-white/5 shrink-0 pb-24 md:pb-3 relative z-50" style={{ background: '#1F2C33' }}>
            {/* Image preview */}
            {imagePreview && (
              <div className="mb-2 relative inline-block" data-testid="image-preview-container">
                <img src={imagePreview} alt="Vista previa" className="max-h-24 rounded-md border border-white/10" />
                <button
                  onClick={clearImage}
                  className="absolute -top-2 -right-2 bg-[#DC2626] text-white rounded-full w-5 h-5 flex items-center justify-center"
                  data-testid="clear-image-btn"
                >
                  <X size={12} weight="bold" />
                </button>
              </div>
            )}
            {uploading && (
              <div className="mb-2 flex items-center gap-2 text-xs text-slate-400">
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-[#1D4ED8] border-t-transparent"></div>
                Subiendo imagen...
              </div>
            )}

            {/* AI Suggestions popover */}
            {showAiSuggestions && (
              <div
                className="mb-2 p-3 bg-[#0F172A] border border-[#1D4ED8]/40 rounded-lg shadow-lg"
                data-testid="ai-suggestions-panel"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-xs font-bold text-[#A78BFA]">
                    <Sparkle size={14} weight="fill" /> SUGERENCIAS IA
                  </div>
                  <button
                    onClick={() => { setShowAiSuggestions(false); setAiSuggestions([]); }}
                    className="text-slate-400 hover:text-white"
                    data-testid="close-ai-suggestions-btn"
                  >
                    <X size={14} weight="bold" />
                  </button>
                </div>
                {loadingAiSuggestions ? (
                  <div className="flex items-center gap-2 text-xs text-slate-300 py-2">
                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-[#A78BFA] border-t-transparent"></div>
                    Redactando 3 opciones con GPT-5.2...
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {aiSuggestions.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => applyAiSuggestion(s)}
                        className="w-full text-left p-2 bg-[#1E293B] hover:bg-[#1D4ED8]/30 border border-white/5 hover:border-[#A78BFA]/40 rounded-md text-xs text-slate-100 transition-colors"
                        data-testid={`ai-suggestion-${i}`}
                      >
                        <span className="inline-block px-1.5 py-0.5 mr-2 text-[10px] font-bold bg-[#A78BFA]/20 text-[#A78BFA] rounded">
                          {i === 0 ? 'CIERRE' : i === 1 ? 'CONSULTIVA' : 'BREVE'}
                        </span>
                        <span className="whitespace-pre-wrap">{s}</span>
                      </button>
                    ))}
                    <button
                      onClick={requestAiSuggestions}
                      className="text-[11px] text-[#A78BFA] hover:underline mt-1"
                      data-testid="regenerate-ai-suggestions-btn"
                    >
                      ↻ Regenerar
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-1.5">
              <button
                onClick={() => setShowTemplates(!showTemplates)}
                className="px-2 py-2 border border-white/10 text-xs font-bold text-white rounded-sm hover:bg-[#0F172A] transition-colors shrink-0"
                data-testid="toggle-templates-btn"
              >
                TPL
              </button>
              <button
                onClick={requestAiSuggestions}
                disabled={loadingAiSuggestions || !messages.length}
                className="px-2 py-2 border border-[#A78BFA]/30 text-[#A78BFA] rounded-sm hover:bg-[#A78BFA]/10 transition-colors shrink-0 disabled:opacity-40"
                title="Sugerir respuesta con IA"
                data-testid="ai-suggest-reply-btn"
              >
                <Sparkle size={16} weight={loadingAiSuggestions ? 'duotone' : 'fill'} className={loadingAiSuggestions ? 'animate-pulse' : ''} />
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageSelect}
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                data-testid="image-file-input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-2 py-2 border border-white/10 text-slate-400 rounded-sm hover:bg-[#0F172A] hover:text-[#1D4ED8] transition-colors shrink-0"
                disabled={loading || selectedConversation?.platform !== 'messenger'}
                title={selectedConversation?.platform !== 'messenger' ? 'Solo disponible para Messenger' : 'Adjuntar imagen'}
                data-testid="attach-image-btn"
              >
                <ImageIcon size={18} weight="bold" />
              </button>
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(newMessage); }
                }}
                placeholder="Escribe un mensaje..."
                className="flex-1 min-w-0 px-4 py-2.5 bg-[#2A3942] border border-white/10 text-white placeholder:text-slate-400 text-sm rounded-full focus:ring-2 focus:ring-[#25D366] focus:outline-none"
                disabled={loading}
                data-testid="message-input"
              />
              <button
                onClick={() => sendMessage(newMessage)}
                disabled={loading || (!newMessage.trim() && !imageFile)}
                className="w-10 h-10 bg-[#25D366] hover:bg-[#20B858] active:scale-95 text-white rounded-full flex items-center justify-center transition-all disabled:opacity-50 shrink-0 shadow-md"
                data-testid="send-message-btn"
              >
                <PaperPlaneRight size={18} weight="fill" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className={`flex-1 flex items-center justify-center ${mobileShowChat ? 'flex' : 'hidden md:flex'}`} style={{ background: '#0B141A' }}>
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'rgba(37,211,102,0.1)' }}>
              <WhatsappLogo size={42} weight="fill" className="text-[#25D366]" />
            </div>
            <p className="text-base font-bold text-slate-200 mb-1">CRM SACH Mensajes</p>
            <p className="text-sm text-slate-400">Selecciona una conversación para empezar</p>
          </div>
        </div>
      )}

      {/* ASSIGN DIALOG */}
      <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Asignar Conversacion</DialogTitle>
            <DialogDescription>Selecciona un usuario para asignar esta conversacion</DialogDescription>
          </DialogHeader>
          <div className="space-y-4" data-testid="quick-assign-dialog">
            <p className="text-sm text-slate-400">Selecciona el usuario al que deseas asignar esta conversacion</p>
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start" onClick={() => handleAssignConversation(null)} data-testid="unassign-btn">
                Sin asignar
              </Button>
              {users.map((u) => (
                <Button
                  key={u.id}
                  variant="outline"
                  className={`w-full justify-start ${selectedConversation?.assigned_to === u.id ? 'bg-[#1D4ED8]/10 border-[#1D4ED8]' : ''}`}
                  onClick={() => handleAssignConversation(u.id)}
                  data-testid={`quick-assign-to-${u.id}`}
                >
                  {u.name} ({u.email || u.username})
                </Button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ORDER CREATION DIALOG */}
      <Dialog open={isOrderDialogOpen} onOpenChange={setIsOrderDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Crear Pedido desde Conversacion</DialogTitle>
            <DialogDescription>Completa los datos del pedido para este cliente</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateOrder} className="space-y-4" data-testid="inbox-order-form">
            <div>
              <Label>Productos</Label>
              {orderForm.items.map((item, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <Input
                    placeholder="Producto"
                    value={item.name}
                    onChange={(e) => updateOrderItem(idx, 'name', e.target.value)}
                    className="flex-1"
                    data-testid={`order-item-name-${idx}`}
                  />
                  <Input
                    type="number"
                    placeholder="Cant."
                    value={item.quantity === 0 ? '' : item.quantity}
                    onChange={(e) => {
                      const v = e.target.value;
                      updateOrderItem(idx, 'quantity', v === '' ? '' : (parseInt(v) || 0));
                    }}
                    className="w-20"
                  />
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Precio"
                    value={item.price === 0 ? '' : item.price}
                    onChange={(e) => {
                      const v = e.target.value;
                      updateOrderItem(idx, 'price', v === '' ? '' : (parseFloat(v) || 0));
                    }}
                    className="w-24"
                  />
                  {orderForm.items.length > 1 && (
                    <Button type="button" variant="outline" onClick={() => removeOrderItem(idx)} className="px-3 shrink-0">
                      <X size={14} />
                    </Button>
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addOrderItem} className="w-full mt-1" size="sm">
                + Agregar Item
              </Button>
            </div>

            <div>
              <Label>Metodo de Pago</Label>
              <select
                value={orderForm.payment_method}
                onChange={(e) => setOrderForm(prev => ({ ...prev, payment_method: e.target.value }))}
                className="w-full text-sm p-2 bg-[#0F172A] text-white border border-white/10 rounded-sm focus:ring-2 focus:ring-[#1D4ED8] focus:outline-none"
                data-testid="inbox-order-payment"
              >
                <option value="cash">Efectivo</option>
                <option value="deposit">Deposito</option>
                <option value="card">Tarjeta</option>
              </select>
            </div>

            <div>
              <Label>Direccion de Entrega</Label>
              <Textarea
                value={orderForm.delivery_address}
                onChange={(e) => setOrderForm(prev => ({ ...prev, delivery_address: e.target.value }))}
                rows={2}
                data-testid="inbox-order-address"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Telefono 1 *</Label>
                <Input
                  value={orderForm.phone1}
                  onChange={(e) => setOrderForm(prev => ({ ...prev, phone1: e.target.value }))}
                  placeholder="Numero principal"
                  required
                  data-testid="inbox-order-phone1"
                />
              </div>
              <div>
                <Label>Telefono 2 <span className="text-xs text-slate-400">(opcional)</span></Label>
                <Input
                  value={orderForm.phone2}
                  onChange={(e) => setOrderForm(prev => ({ ...prev, phone2: e.target.value }))}
                  placeholder="Numero secundario"
                  data-testid="inbox-order-phone2"
                />
              </div>
            </div>

            <div>
              <Label>Empresa Courier</Label>
              <Input
                value={orderForm.courier_company}
                onChange={(e) => setOrderForm(prev => ({ ...prev, courier_company: e.target.value }))}
                placeholder="Nombre del courier"
                data-testid="inbox-order-courier"
              />
            </div>

            <div>
              <Label>Valor de Envio (Q)</Label>
              <Input
                type="number"
                step="0.01"
                value={orderForm.shipping_cost === 0 ? '' : orderForm.shipping_cost}
                onChange={(e) => {
                  const v = e.target.value;
                  setOrderForm(prev => ({ ...prev, shipping_cost: v === '' ? '' : (parseFloat(v) || 0) }));
                }}
                placeholder="0.00"
                data-testid="inbox-order-shipping-cost"
              />
            </div>

            <div>
              <Label>Notas</Label>
              <Textarea
                value={orderForm.notes}
                onChange={(e) => setOrderForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                data-testid="inbox-order-notes"
              />
            </div>

            <div className="pt-3 border-t border-white/10">
              <p className="text-sm text-slate-400">
                Subtotal: Q{orderForm.items.reduce((s, i) => s + ((Number(i.quantity) || 0) * (Number(i.price) || 0)), 0).toFixed(2)}
              </p>
              {parseFloat(orderForm.shipping_cost) > 0 && (
                <p className="text-sm text-slate-400">Envio: Q{parseFloat(orderForm.shipping_cost).toFixed(2)}</p>
              )}
              <p className="text-lg font-bold text-white">
                Total: Q{(orderForm.items.reduce((s, i) => s + ((Number(i.quantity) || 0) * (Number(i.price) || 0)), 0) + (parseFloat(orderForm.shipping_cost) || 0)).toFixed(2)}
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsOrderDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#16A34A] hover:bg-[#15803D]" data-testid="submit-inbox-order-btn">
                <ShoppingCart size={16} weight="bold" className="mr-1" />
                Crear Pedido
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Close Conversation Dialog */}
      <Dialog open={isCloseDialogOpen} onOpenChange={setIsCloseDialogOpen}>
        <DialogContent className="bg-[#0F172A] border-white/10 text-white" data-testid="close-conversation-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <XCircle size={20} weight="bold" className="text-red-400" />
              Cerrar conversación
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Selecciona el motivo. Los mensajes NO se eliminan, solo se marca como cerrada. Si el cliente vuelve a escribir, se reabrirá automáticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {[
              { value: 'venta', label: '✅ Venta cerrada', color: 'emerald' },
              { value: 'no_compro', label: '❌ Cliente no compró', color: 'amber' },
              { value: 'spam', label: '🚫 Spam / Equivocó número', color: 'red' },
              { value: 'inactividad', label: '💤 Sin respuesta / Inactividad', color: 'slate' },
              { value: 'otro', label: '📁 Otro motivo', color: 'blue' },
            ].map(r => (
              <button
                key={r.value}
                type="button"
                onClick={() => setCloseReason(r.value)}
                className={`w-full text-left text-sm px-4 py-3 rounded-[10px] font-bold border transition-all ${
                  closeReason === r.value
                    ? 'bg-[#1D4ED8] border-[#1D4ED8] text-white shadow-[0_0_15px_rgba(29,78,216,0.4)]'
                    : 'bg-[#111827] border-white/10 text-slate-300 hover:border-[#1D4ED8]/50'
                }`}
                data-testid={`close-reason-${r.value}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
            <Button variant="outline" onClick={() => setIsCloseDialogOpen(false)} disabled={closing}>Cancelar</Button>
            <Button onClick={handleCloseConversation} disabled={closing} className="bg-red-600 hover:bg-red-500" data-testid="confirm-close-conversation-btn">
              {closing ? 'Cerrando...' : 'Cerrar conversación'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inbox;
